import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { NetworkId, WalletManager, WalletProvider } from '@txnlab/use-wallet-react';
import { trackEvent } from './lib/analytics';
import { walletConfig } from './walletConfig';
import WalletConnect from './components/WalletConnect';
import AuthModal from './components/AuthModal';
import { useState, lazy, Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AchievementTracker from './components/AchievementTracker';

// Lazy load pages for performance (Chapter 11 optimization)
const Home = lazy(() => import('./pages/Home.tsx'));
const Play = lazy(() => import('./pages/Play.tsx'));
const Profile = lazy(() => import('./pages/Profile.tsx'));
const Leaderboards = lazy(() => import('./pages/Leaderboards.tsx'));
const Multiplayer = lazy(() => import('./pages/Multiplayer.tsx'));
const Help = lazy(() => import('./pages/Help.tsx'));
const Adventure = lazy(() => import('./pages/Adventure.tsx'));
const MatchResult = lazy(() => import('./pages/MatchResult.tsx'));
const PublicProfile = lazy(() => import('./pages/PublicProfile.tsx'));
const Admin = lazy(() => import('./pages/Admin.tsx'));
const Tournament = lazy(() => import('./pages/Tournament.tsx'));
const TournamentDetail = lazy(() => import('./pages/TournamentDetail.tsx'));
const DiscordCallback = lazy(() => import('./pages/DiscordCallback.tsx'));
const TermsOfService = lazy(() => import('./pages/TermsOfService.tsx'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy.tsx'));
const Devlog = lazy(() => import('./pages/Devlog.tsx'));
const Blog = lazy(() => import('./pages/Blog.tsx'));

const PageLoader = () => (
  <div className="fixed-center bg-pit-black z-[9999]">
    <div className="pit-loader"></div>
  </div>
);

import { ADMIN_WALLETS } from './lib/constants';

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { activeAccount, isVerified } = useAuth();
  const isAdmin = activeAccount && isVerified && ADMIN_WALLETS.includes(activeAccount.address);

  if (!isAdmin) {
    return (
      <div className="pit-bg min-vh-nav flex items-center justify-center p-4 text-center">
        <div className="glassmorphism p-12 rounded-[3rem] border-t-4 border-red-500 max-w-lg">
          <span className="text-6xl mb-4 block">🚫</span>
          <h1 className="text-3xl font-black italic text-white uppercase tracking-tighter mb-4">Access Denied</h1>
          <p className="text-slate-400 font-medium mb-8">This portal is restricted to authorized pickle administrators.</p>
          <Link to="/" className="pit-button">Return Home</Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AuthenticatedApp() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { activeAccount, isVerified, showAuthModal, setShowAuthModal } = useAuth();
  const isAdmin = activeAccount && isVerified && ADMIN_WALLETS.includes(activeAccount.address);

  const isGameActive = ['/play', '/multiplayer', '/adventure'].includes(location.pathname);

  // Phase 2 Optimization: Defer GTM loading to improve LCP/FID
  useEffect(() => {
    const loadGTM = () => {
      if (window.gtmLoaded) return;
      window.gtmLoaded = true;
      const script = document.createElement("script");
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-74V45L87LV";
      script.async = true;
      document.head.appendChild(script);

      // Inline config after script load
      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function(){ window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', 'G-74V45L87LV');
      };
    };

    // Load after 3s or first user interaction
    const timer = setTimeout(loadGTM, 3000);
    window.addEventListener('mousedown', loadGTM, { once: true });
    window.addEventListener('touchstart', loadGTM, { once: true });
    window.addEventListener('keydown', loadGTM, { once: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', loadGTM);
      window.removeEventListener('touchstart', loadGTM);
      window.removeEventListener('keydown', loadGTM);
    };
  }, []);

  const navLinks = [
    { label: 'Play',         to: '/',             icon: <><path d="m11 17 2 2 4-4"/><path d="m5 10 2-2 4 4"/><path d="M7 10h10v10H7z"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></> },
    { label: 'Leaderboards', to: '/leaderboards', icon: <><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></> },
    { label: 'Profile',      to: '/profile',      icon: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></> },
    { label: 'Help',         to: '/help',         icon: <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></> },
  ];

  const desktopLinkClass = "hover:text-pit-green transition-colors font-bold uppercase text-[10px] tracking-[0.15em]";

  return (
    <div className="flex flex-col min-h-screen w-full">
        {/* Navigation Bar */}
        <nav className="glassmorphism flex items-center justify-between px-4 md:px-6 py-3 md:py-4 fixed top-0 w-full z-50">
          <Link to="/" className="text-xl md:text-2xl font-black italic tracking-tighter text-white" onClick={() => setMenuOpen(false)}>
            <span className="neon-text" style={{ color: '#39FF14' }}>Pickle</span>Pong
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex gap-8 items-center text-slate-400">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} className={desktopLinkClass}>{link.label}</Link>
            ))}
            {isAdmin && (
              <Link to="/admin" className="text-pit-green hover:brightness-125 transition-all font-black uppercase text-[10px] tracking-[0.15em] border border-pit-green/30 px-3 py-1.5 rounded-lg bg-pit-green/5">Admin</Link>
            )}
            <WalletConnect />
          </div>

          {/* Mobile Right Side: Wallet + Hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <WalletConnect />
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="flex flex-col justify-center items-center gap-[5px] w-8 h-8 group"
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 bg-slate-300 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
              <span className={`block w-6 h-0.5 bg-slate-300 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-6 h-0.5 bg-slate-300 transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
            </button>
          </div>
        </nav>

        {menuOpen && (
          <div className="md:hidden fixed top-[52px] left-0 w-full z-[100] glassmorphism border-t border-[#39FF14]/20 flex flex-col py-6 px-6 gap-6 text-slate-300 font-bold text-xs uppercase tracking-widest bg-black/80 backdrop-blur-3xl shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-pit-green/[0.03] to-transparent pointer-events-none" />
            
            {navLinks.map(link => (
              <Link 
                key={link.to} 
                to={link.to} 
                className="hover:text-pit-green transition-all py-2 border-b border-white/5 flex items-center justify-between group active:scale-[0.98]" 
                onClick={() => setMenuOpen(false)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2.5 rounded-full bg-white/5 border border-white/10 group-hover:border-pit-green/40 group-hover:bg-pit-green/10 transition-all shadow-lg active:scale-90">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {link.icon}
                    </svg>
                  </div>
                  <span>{link.label}</span>
                </div>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" className="opacity-0 group-hover:opacity-40 transition-opacity">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </Link>
            ))}

            {isAdmin && (
              <Link 
                to="/admin" 
                className="text-pit-green hover:text-white transition-all py-2 border-b border-white/5 flex items-center justify-between group active:scale-[0.98]" 
                onClick={() => setMenuOpen(false)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2.5 rounded-full bg-pit-green/10 border border-pit-green/30 shadow-[0_0_15px_rgba(57,255,20,0.1)] active:scale-90 transition-all">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                    </svg>
                  </div>
                  <span>ADMIN PORTAL</span>
                </div>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" className="opacity-20 transition-opacity">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </Link>
            )}
          </div>
        )}

        {/* Main Content Area */}
        <main className={`flex-1 ${isGameActive ? 'mt-12 md:mt-16' : 'mt-14 md:mt-16'} overflow-y-auto`}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/adventure" element={<Adventure />} />
              <Route path="/play" element={<Play />} />
              <Route path="/multiplayer" element={<Multiplayer />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/leaderboards" element={<Leaderboards />} />
              <Route path="/tournament" element={<Tournament />} />
              <Route path="/tournament/:id" element={<TournamentDetail />} />
              <Route path="/marketplace" element={<Navigate to="/profile?tab=arenas" replace />} />
              <Route path="/help" element={<Help />} />
              <Route path="/match/:matchId" element={<MatchResult />} />
              <Route path="/u/:username" element={<PublicProfile />} />
              <Route path="/player/:username" element={<PublicProfile />} />
              <Route path="/admin" element={<AdminGuard><Suspense fallback={<PageLoader />}><Admin /></Suspense></AdminGuard>} />
              <Route path="/auth/discord/callback" element={<Suspense fallback={<PageLoader />}><DiscordCallback /></Suspense>} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/devlog" element={<Devlog />} />
              <Route path="/blog" element={<Blog />} />
            </Routes>
          </Suspense>
        </main>

        {/* Footer - Hidden when playing to maximize screen space on mobile */}
        {!isGameActive && (
          <footer className="glassmorphism border-t border-[#39FF14]/10 py-4 px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-bold uppercase tracking-widest text-slate-500 min-h-[64px]">
            <div className="flex items-center gap-6">
              <a
                href="https://discord.gg/dxE73H8JTr"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('Footer_Discord')}
                className="hover:text-[#39FF14] transition-colors flex items-center gap-2"
              >
                <span>Discord</span>
              </a>
              <a
                href="https://x.com/LoafPickle"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('Footer_X_Twitter')}
                className="hover:text-[#39FF14] transition-colors flex items-center gap-2"
              >
                <span>X (Twitter)</span>
              </a>
              <Link to="/devlog" onClick={() => trackEvent('Footer_Devlog')} className="hover:text-[#39FF14] transition-colors flex items-center gap-2"><span>Devlog</span></Link>
              <Link to="/blog" onClick={() => trackEvent('Footer_Blog')} className="hover:text-[#39FF14] transition-colors flex items-center gap-2"><span>Blog</span></Link>
              <Link to="/terms" onClick={() => trackEvent('Footer_Terms')} className="hover:text-[#39FF14] transition-colors flex items-center gap-2"><span>Terms</span></Link>
              <Link to="/privacy" onClick={() => trackEvent('Footer_Privacy')} className="hover:text-[#39FF14] transition-colors flex items-center gap-2"><span>Privacy</span></Link>
            </div>
            <div className="flex flex-col md:items-end gap-1">
              <div className="flex items-center gap-2">
                <span>© 2026</span>
                <span className="text-white">LoafPickle Worldwide, LLC</span>
              </div>
              <div className="text-[10px] text-slate-600 font-black">All Rights Reserved</div>
            </div>
          </footer>
        )}

        <AchievementTracker />
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
    </div>
  );
}

declare global {
  interface Window {
    gtmLoaded?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
  }
}

let walletManager: WalletManager | null = null;
try {
  walletManager = new WalletManager(walletConfig);
  walletManager.setActiveNetwork(NetworkId.MAINNET);
} catch (err) {
  console.error('[WalletManager] Failed to initialize:', err);
}

function App() {
  if (!walletManager) return null;

  return (
    <WalletProvider manager={walletManager}>
        <AuthProvider>
          <Router>
            <AuthenticatedApp />
          </Router>
        </AuthProvider>
    </WalletProvider>
  );
}

export default App;
